import {constant} from '@packages/utilities/fp/utils';
import {weakMemo} from '@packages/utilities/memo';

import type {BreakpointID} from '@packages/systems/style/types';
import type {SelectorToken} from '@packages/systems/style-legacy/rules';
import type {
  CssSelectorValue,
  DenormalizedChunk,
  DenormalizedToken,
  StyleProperty,
  StyleStore$ValueType,
} from './types';

export type PropertyOriginRule = {
  tokens: SelectorToken[];
  mediaQueryKey: BreakpointID;
};

/**
 *
 * A description of where a property comes from, relative to
 * the current context.
 *
 * There are 4 basic sub-types:
 *
 * #### `{type: 'default', ...}`
 *
 * The property is inherited from the element or component's defaults.
 *
 * #### `{type: 'inherited', ...}`
 *
 * The property is inherited from a less-specific context.
 *
 * Examples:
 *
 * - The property is set on a less-specific media query (i.e. `laptop`) when
 *   a more-specific media query (i.e. `phone`) is active.
 * - The property is set on a base class (i.e. `Button`) when a combo-class
 *   (i.e. `Button Secondary`) is active.
 *
 * #### `{type: 'local', ...}`
 *
 * The property comes from the current active context.
 *
 * Examples:
 *
 * - The currently-selected class (i.e. `Button Secondary`) is the most-specific
 *   selector that affects the currently-selected element.
 * - (In the Future) The selected component instance overrides the master component's
 *   default styles.
 *
 * #### `{type: 'overridden', ...}`
 *
 * The property is set on the current local context, however a _more specific_ context
 * overrides the value set in this context.
 *
 * Examples:
 *
 * - While the `phone` media query is active, but the `laptop` rule is selected, and
 *   both media queries affect the property. The `phone` media query overrides the
 *   currently-selected `laptop` rule.
 * - The property is set on a combo class (i.e. `Button Secondary`), and also set on the
 *   base class (i.e. `Button`), and the base class' rule is currently selected. The
 *   combo class overrides the currently-selected base-class rule.
 *
 */

type Default$Origin = {
  type: 'default';
  rule: null;
};

type Inherited$Origin = {
  type: 'inherited';
  /**
   * Details about the rule that this style property is inherited from.
   */
  rule: PropertyOriginRule;
};

type Local$Origin = {
  type: 'local';
  /**
   * Details about the rule that this style property will inherit from
   * if the locally-applied style property is reset.
   */
  rule: PropertyOriginRule | null;
  /**
   * A flag that signifies that this property should have a warning color
   */
  hasWarning?: boolean;
};

type Overridden$Origin = {
  type: 'overridden';
  /**
   * Details about the rule that this style property is overridden by.
   */
  rule: PropertyOriginRule;
};

type Id$Origin = {
  type: 'id';
  /**
   * Details about the rule that this style property will inherit from
   * if the locally-applied style property is reset.
   */
  rule: PropertyOriginRule | null;
};

export type PropertyOrigin =
  | Default$Origin
  | Inherited$Origin
  | Local$Origin
  | Overridden$Origin
  | Id$Origin;

/**
 * The classification of where a property comes from, relative to
 * the current context.
 */
export type PropertyOriginType = PropertyOrigin['type'];

const idRule: Id$Origin = {type: 'id', rule: null};

const _IdOrigin = weakMemo(
  (rule: PropertyOriginRule): Id$Origin => ({
    type: 'id',
    rule,
  })
);

export const IdOrigin = (rule: PropertyOriginRule | null): Id$Origin =>
  rule ? _IdOrigin(rule) : idRule;

const localRule: Local$Origin = {type: 'local', rule: null};

const _LocalOrigin = weakMemo(
  (rule: PropertyOriginRule): Local$Origin => ({
    type: 'local',
    rule,
  })
);

export const LocalOrigin = (rule: PropertyOriginRule | null): Local$Origin =>
  rule ? _LocalOrigin(rule) : localRule;

export const DefaultOrigin: () => Default$Origin = constant({
  type: 'default',
  rule: null,
});

export const InheritedOrigin = weakMemo(
  (rule: PropertyOriginRule): Inherited$Origin => ({
    type: 'inherited',
    rule,
  })
);

export const OverriddenOrigin = weakMemo(
  (rule: PropertyOriginRule): Overridden$Origin => ({
    type: 'overridden',
    rule,
  })
);

const hasIdOrigin = <T extends StyleStore$ValueType>(
  property: StyleProperty<T>
): boolean => property.id.value !== null;

export const hasLocalOrigin = <T extends StyleStore$ValueType>(
  property: StyleProperty<T>
): boolean => property.local.value !== null;

export const hasUpstreamOrigin = <T extends StyleStore$ValueType>(
  property: StyleProperty<T>
): boolean => property.upstream.value !== null;

export const hasDownstreamOrigin = <T extends StyleStore$ValueType>(
  property: StyleProperty<T>
): boolean => property.downstream.value !== null;

export const valueFrom = <
  T extends StyleStore$ValueType,
  P extends StyleProperty<T>,
>(
  property: P
): P['valueFrom'] => {
  if (property.valueFrom) {
    return property.valueFrom;
  }
  return null;
};

export const valueOverride = <
  T extends StyleStore$ValueType,
  P extends StyleProperty<T>,
>(
  property: P
): P['valueOverride'] => {
  if (property.valueOverride) {
    return property.valueOverride;
  }
  return null;
};

// [QUESTION]
// other CssSelector examples take chunks, mediaQueryKey
// PropertyOriginRule requires tokens, mediaQueryKey
// what happens if chunks.length > 1? flatten array of tokens?
const toPropertyOriginRule = weakMemo(
  ({chunks, mediaQueryKey}: CssSelectorValue): PropertyOriginRule => ({
    tokens: chunks.reduce<SelectorToken[]>(
      (allTokens, {tokens}: DenormalizedChunk) => [
        ...allTokens,
        ...tokens.map(
          ({
            isTag,
            label,
            pseudo,
            state,
            variantState,
            namespace,
            origin,
          }: DenormalizedToken): SelectorToken => ({
            label,
            className: isTag ? undefined : label,
            tagName: isTag ? label : undefined,
            pseudoState: pseudo ? pseudo : undefined,
            customNodeState: state ? state : undefined,
            variantState: variantState ? variantState : undefined,
            namespace: namespace ? namespace : undefined,
            origin: origin ? origin : undefined,
          })
        ),
      ],
      []
    ),
    mediaQueryKey,
  })
);

export const getPropertyOrigin = <T extends StyleStore$ValueType>(
  property: StyleProperty<T>
): PropertyOrigin => {
  // overridden rule
  if (hasDownstreamOrigin(property)) {
    const override = valueOverride(property);
    return override
      ? OverriddenOrigin(toPropertyOriginRule(override))
      : DefaultOrigin();
  } else if (hasIdOrigin(property)) {
    // NOTE: Updating this to do the same check done by connect.js#getOrigin
    // Not sure if this check is correct but prevents
    // breaking existing code while we figure it out.
    if (hasUpstreamOrigin(property)) {
      const from = valueFrom(property);
      return from ? IdOrigin(toPropertyOriginRule(from)) : IdOrigin(null);
    } else {
      return IdOrigin(null);
    }
  } else if (hasLocalOrigin(property)) {
    // NOTE: Updating this to do the same check done by connect.js#getOrigin
    // Not sure if this check is correct but prevents
    // breaking existing code while we figure it out.
    if (hasUpstreamOrigin(property)) {
      const from = valueFrom(property);
      return from ? LocalOrigin(toPropertyOriginRule(from)) : LocalOrigin(null);
    } else {
      return LocalOrigin(null);
    }
  } else if (hasUpstreamOrigin(property)) {
    // inherited rule and local can both exist but local wins
    const from = valueFrom(property);
    return from ? InheritedOrigin(toPropertyOriginRule(from)) : DefaultOrigin();
  } else {
    return DefaultOrigin();
  }
};

// TODO: when we cleanup property origin logic into a module use constants
export const getPropertyOriginType = <T extends StyleStore$ValueType>(
  property: StyleProperty<T>
): PropertyOriginType => {
  if (hasDownstreamOrigin(property)) {
    return 'overridden';
  } else if (hasIdOrigin(property)) {
    return 'id';
  } else if (hasLocalOrigin(property)) {
    return 'local';
  } else if (hasUpstreamOrigin(property)) {
    return 'inherited';
  } else {
    return 'default';
  }
};

export const originWeights = {
  overridden: 5,
  id: 4,
  local: 3,
  inherited: 2,
  default: 1,
} as const;

// Given a group of StyleProperty find the highest priority origin.
export const getCompoundPropertyOrigin = <T extends StyleStore$ValueType>(
  ...properties: Array<StyleProperty<T>>
): PropertyOrigin => {
  let priorityOrigin: PropertyOrigin = DefaultOrigin();

  for (const property of properties) {
    const origin = getPropertyOrigin(property);
    // [TODO]: do we want local or overridden to be highest priority for compound?
    // overridden origin is highest priority so we can short circuit
    if (origin.type === 'overridden') {
      return origin;
    }
    // compare origin priorities
    else if (originWeights[origin.type] > originWeights[priorityOrigin.type]) {
      priorityOrigin = origin;
    }
  }
  return priorityOrigin;
};

// Find the highest priority origin from a list of origins.
export const getPriorityOrigin = (
  ...origins: Array<PropertyOrigin>
): PropertyOrigin => {
  let priorityOrigin: PropertyOrigin = DefaultOrigin();
  for (const origin of origins) {
    if (origin.type === 'overridden') {
      return origin;
    }
    // compare origin priorities
    else if (originWeights[origin.type] > originWeights[priorityOrigin.type]) {
      priorityOrigin = origin;
    }
  }
  return priorityOrigin;
};
